import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { dataProvider } from '@/lib/dataProvider';
import toast from 'react-hot-toast';

const ClassDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'overview' | 'students' | 'schedule' | 'materials' | 'attendance' | 'assessment'>('overview');
  const [selectedSchedule, setSelectedSchedule] = useState<string | null>(null);
  const [attendanceData, setAttendanceData] = useState<any>({});
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false);
  const [fileUploadSchedule, setFileUploadSchedule] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedImportSession, setSelectedImportSession] = useState<string>('');
  const [importFile, setImportFile] = useState<File | null>(null);

  // Assessment state
  const [assessmentData, setAssessmentData] = useState<any>({});
  const [selectedAssessment, setSelectedAssessment] = useState<string | null>(null);
  const [showAssessmentImportModal, setShowAssessmentImportModal] = useState(false);
  const [selectedImportAssessment, setSelectedImportAssessment] = useState<string>('');
  const [assessmentImportFile, setAssessmentImportFile] = useState<File | null>(null);
  const [showCreateAssessmentModal, setShowCreateAssessmentModal] = useState(false);

  // Get assessments from data provider
  const mockAssessments: any[] = dataProvider.getAssessments();

  // Get class data from data provider
  const getClassData = (classId: string): any | null => {
    return dataProvider.getClassDetail(classId);
  };

  if (!id) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-600 mb-4">Không tìm thấy ID lớp học</div>
          <Link to="/lms/classes" className="text-indigo-600 hover:text-indigo-900">
            Quay lại danh sách lớp học
          </Link>
        </div>
      </div>
    );
  }

  const classData = getClassData(id);

  if (!classData || !classData.organization || !classData.students || !classData.schedules) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-600 mb-4">Không tìm thấy lớp học hoặc dữ liệu không đầy đủ</div>
          <Link to="/lms/classes" className="text-indigo-600 hover:text-indigo-900">
            Quay lại danh sách lớp học
          </Link>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-3 py-1 text-sm font-medium rounded-full";
    switch (status) {
      case 'upcoming':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'active':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'completed':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return baseClasses;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'upcoming': return 'Sắp diễn ra';
      case 'active': return 'Đang diễn ra';
      case 'completed': return 'Đã hoàn thành';
      case 'cancelled': return 'Đã hủy';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStudentStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'active': return `${baseClasses} bg-green-100 text-green-800`;
      case 'completed': return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'dropped': return `${baseClasses} bg-red-100 text-red-800`;
      case 'pending': return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default: return baseClasses;
    }
  };

  const getStudentStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Đang học';
      case 'completed': return 'Hoàn thành';
      case 'dropped': return 'Đã bỏ học';
      case 'pending': return 'Chờ xác nhận';
      default: return status;
    }
  };

  const getCourseTypeBadge = (type: string) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (type) {
      case 'elearning': return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'online': return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'offline': return `${baseClasses} bg-green-100 text-green-800`;
      default: return baseClasses;
    }
  };

  const getCourseTypeText = (type: string) => {
    switch (type) {
      case 'elearning': return 'E-learning';
      case 'online': return 'Trực tuyến';
      case 'offline': return 'Tại lớp';
      default: return type;
    }
  };

  const getCourseTypeIcon = (type: string) => {
    switch (type) {
      case 'elearning': return 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z';
      case 'online': return 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z';
      case 'offline': return 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4';
      default: return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
    }
  };

  const getOrganizationTypeBadge = (type: string) => {
    const baseClasses = "px-2 py-1 text-xs font-semibold rounded-full";
    switch (type) {
      case 'internal':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'external':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'partner':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      default:
        return baseClasses;
    }
  };

  const getOrganizationTypeText = (type: string) => {
    switch (type) {
      case 'internal': return 'Nội bộ';
      case 'external': return 'Bên ngoài';
      case 'partner': return 'Đối tác';
      default: return type;
    }
  };

  // Add attendance management functions
  const markAttendance = (scheduleId: string, studentId: string, status: any, method: any = 'manual', note?: string) => {
    setAttendanceData((prev: any) => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        [studentId]: {
          studentId,
          scheduleId,
          status,
          method,
          note,
          timestamp: new Date().toISOString()
        }
      }
    }));
  };

  const bulkMarkAttendance = (scheduleId: string, records: any[]) => {
    const scheduleAttendance: { [studentId: string]: any } = {};
    records.forEach((record: any) => {
      scheduleAttendance[record.studentId] = record;
    });
    
    setAttendanceData((prev: any) => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        ...scheduleAttendance
      }
    }));
  };

  const handleExcelUpload = (scheduleId: string, file: File) => {
    // Mock Excel processing - in real app, you'd use a library like xlsx
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Mock parsing Excel data
        const mockAttendanceRecords: any[] = classData!.students.map((student: any) => ({
          studentId: student.id,
          scheduleId,
          status: Math.random() > 0.2 ? 'present' : 'absent' as any,
          method: 'excel' as const,
          timestamp: new Date().toISOString()
        }));
        
        bulkMarkAttendance(scheduleId, mockAttendanceRecords);
        setFileUploadSchedule(null);
        toast.success('Đã nhập điểm danh từ file Excel thành công!');
      } catch (error) {
        toast.error('Lỗi khi đọc file Excel. Vui lòng kiểm tra định dạng file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImportAttendance = () => {
    if (!importFile || !selectedImportSession) {
      toast.error('Vui lòng chọn file và buổi học!');
      return;
    }

    // Mock Excel processing - in real app, you'd use a library like xlsx
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Mock parsing Excel data
        const mockAttendanceRecords: any[] = classData!.students.map((student: any) => ({
          studentId: student.id,
          scheduleId: selectedImportSession,
          status: Math.random() > 0.2 ? 'present' : 'absent' as any,
          method: 'excel' as const,
          timestamp: new Date().toISOString()
        }));
        
        bulkMarkAttendance(selectedImportSession, mockAttendanceRecords);
        
        // Reset modal state
        setShowImportModal(false);
        setSelectedImportSession('');
        setImportFile(null);
        
        toast.success('Đã nhập điểm danh từ file Excel thành công!');
      } catch (error) {
        toast.error('Lỗi khi đọc file Excel. Vui lòng kiểm tra định dạng file.');
      }
    };
    reader.readAsArrayBuffer(importFile);
  };

  const downloadTemplate = () => {
    // Mock template download - in real app, this would generate and download an actual Excel template
    const templateData = [
      ['Mã học sinh', 'Họ tên', 'Trạng thái'],
      ...classData!.students.map((student: any) => [student.id, student.name, 'Có mặt'])
    ];
    
    // Create a simple CSV for demonstration
    const csvContent = templateData.map((row: any) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'mau_diem_danh.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const simulateElearningAttendance = (scheduleId: string) => {
    // Simulate automatic e-learning attendance based on learning app signals
    const elearningRecords: any[] = classData!.students.map((student: any) => ({
      studentId: student.id,
      scheduleId,
      status: Math.random() > 0.1 ? 'present' : 'absent' as any,
      method: 'elearning_auto' as const,
      timestamp: new Date().toISOString(),
      note: 'Tự động từ hệ thống E-learning'
    }));
    
    bulkMarkAttendance(scheduleId, elearningRecords);
  };

  const getAttendanceStatusBadge = (status: any) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'present': return `${baseClasses} bg-green-100 text-green-800`;
      case 'absent': return `${baseClasses} bg-red-100 text-red-800`;
      case 'late': return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'excused': return `${baseClasses} bg-blue-100 text-blue-800`;
      default: return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getAttendanceStatusText = (status: any) => {
    switch (status) {
      case 'present': return 'Có mặt';
      case 'absent': return 'Vắng mặt';
      case 'late': return 'Muộn';
      case 'excused': return 'Có phép';
      default: return 'Chưa xác định';
    }
  };

  const calculateAttendanceRate = (scheduleId: string) => {
    const scheduleAttendance = attendanceData[scheduleId];
    if (!scheduleAttendance) return 0;
    
    const total = Object.keys(scheduleAttendance).length;
    const present = Object.values(scheduleAttendance).filter((record: any) => 
      record.status === 'present' || record.status === 'late'
    ).length;
    
    return total > 0 ? Math.round((present / total) * 100) : 0;
  };

  // Assessment management functions
  const addAssessmentScore = (assessmentId: string, studentId: string, score: number, maxScore: number, feedback?: string) => {
    const newResult: any = {
      id: `AR_${assessmentId}_${studentId}_${Date.now()}`,
      assessmentId,
      studentId,
      score,
      maxScore,
      gradedAt: new Date().toISOString(),
      gradedBy: 'Admin', // In real app, get from user context
      feedback,
      status: 'graded',
      attempts: 1
    };

    setAssessmentData((prev: any) => ({
      ...prev,
      [assessmentId]: {
        ...prev[assessmentId],
        [studentId]: newResult
      }
    }));
  };

  const bulkImportAssessmentResults = (assessmentId: string, results: any[]) => {
    const assessmentResults: { [studentId: string]: any } = {};
    results.forEach((result: any) => {
      assessmentResults[result.studentId] = result;
    });
    
    setAssessmentData((prev: any) => ({
      ...prev,
      [assessmentId]: {
        ...prev[assessmentId],
        ...assessmentResults
      }
    }));
  };

  const handleAssessmentExcelUpload = (assessmentId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Mock parsing Excel data
        const assessment = mockAssessments.find(a => a.id === assessmentId);
        if (!assessment) return;

        const mockResults: any[] = classData!.students.map((student: any) => ({
          id: `AR_${assessmentId}_${student.id}_${Date.now()}`,
          assessmentId,
          studentId: student.id,
          score: Math.floor(Math.random() * assessment.maxScore),
          maxScore: assessment.maxScore,
          gradedAt: new Date().toISOString(),
          gradedBy: 'System',
          status: 'graded' as const,
          attempts: 1
        }));
        
        bulkImportAssessmentResults(assessmentId, mockResults);
        toast.success('Đã nhập điểm từ file Excel thành công!');
      } catch (error) {
        toast.error('Lỗi khi đọc file Excel. Vui lòng kiểm tra định dạng file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImportAssessmentResults = () => {
    if (!assessmentImportFile || !selectedImportAssessment) {
      toast.error('Vui lòng chọn file và bài đánh giá!');
      return;
    }

    handleAssessmentExcelUpload(selectedImportAssessment, assessmentImportFile);
    
    // Reset modal state
    setShowAssessmentImportModal(false);
    setSelectedImportAssessment('');
    setAssessmentImportFile(null);
  };

  const downloadAssessmentTemplate = () => {
    const templateData = [
      ['Mã học sinh', 'Họ tên', 'Điểm số', 'Nhận xét'],
      ...classData!.students.map((student: any) => [student.id, student.name, '0', ''])
    ];
    
    const csvContent = templateData.map((row: any) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'mau_nhap_diem.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportAssessmentResults = (assessmentId: string) => {
    const assessment = mockAssessments.find(a => a.id === assessmentId);
    const results = assessmentData[assessmentId];
    
    if (!assessment || !results) {
      toast.error('Không có dữ liệu để xuất!');
      return;
    }

    const exportData = [
      ['Mã học sinh', 'Họ tên', 'Bộ phận', 'Điểm số', 'Điểm tối đa', 'Tỷ lệ %', 'Kết quả', 'Nhận xét', 'Ngày chấm'],
      ...classData!.students.map((student: any) => {
        const result = results[student.id];
        const percentage = result ? Math.round((result.score / result.maxScore) * 100) : 0;
        const isPassed = result ? result.score >= assessment.passingScore : false;
        
        return [
          student.id,
          student.name,
          student.department,
          result ? result.score.toString() : '0',
          assessment.maxScore.toString(),
          `${percentage}%`,
          isPassed ? 'Đạt' : 'Không đạt',
          result?.feedback || '',
          result?.gradedAt ? new Date(result.gradedAt).toLocaleDateString('vi-VN') : ''
        ];
      })
    ];
    
    const csvContent = exportData.map((row: any) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ket_qua_${assessment.title.replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getAssessmentTypeBadge = (type: any) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (type) {
      case 'quiz': return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'exam': return `${baseClasses} bg-red-100 text-red-800`;
      case 'assignment': return `${baseClasses} bg-green-100 text-green-800`;
      case 'project': return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'practical': return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default: return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getAssessmentTypeText = (type: any) => {
    switch (type) {
      case 'quiz': return 'Kiểm tra';
      case 'exam': return 'Kỳ thi';
      case 'assignment': return 'Bài tập';
      case 'project': return 'Dự án';
      case 'practical': return 'Thực hành';
      default: return type;
    }
  };

  const getGradeColor = (score: number, maxScore: number, passingScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= (passingScore / maxScore) * 100) {
      return percentage >= 85 ? 'text-green-600' : 'text-blue-600';
    }
    return 'text-red-600';
  };

  const calculateStudentGPA = (studentId: string) => {
    let totalWeightedScore = 0;
    let totalWeight = 0;

    mockAssessments.forEach((assessment) => {
      const result = assessmentData[assessment.id]?.[studentId];
      if (result) {
        const percentage = (result.score / result.maxScore) * 100;
        totalWeightedScore += percentage * (assessment.weight / 100);
        totalWeight += assessment.weight;
      }
    });

    return totalWeight > 0 ? Math.round(totalWeightedScore / (totalWeight / 100)) : 0;
  };

  const tabs = [
    { id: 'overview', name: 'Tổng quan', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { id: 'students', name: 'Học sinh', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z' },
    { id: 'schedule', name: 'Lịch học', icon: 'M8 7V3a1 1 0 012 0v4h4a1 1 0 010 2h-4v4a1 1 0 01-2 0V9H4a1 1 0 010-2h4z' },
    { id: 'attendance', name: 'Điểm danh', icon: 'M9 5H7a2 2 0 00-2 2v8a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
    { id: 'assessment', name: 'Đánh giá', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { id: 'materials', name: 'Tài liệu', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' }
  ];

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <nav className="flex mb-8" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/lms" className="text-gray-700 hover:text-gray-900">
              LMS
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <Link to="/lms/classes" className="ml-1 text-gray-700 hover:text-gray-900 md:ml-2">
                Lớp học
              </Link>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="ml-1 text-gray-500 md:ml-2" aria-current="page">
                {classData.title}
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="px-6 py-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">{classData.title}</h1>
                <span className={getStatusBadge(classData.status)}>
                  {getStatusText(classData.status)}
                </span>
              </div>
              <p className="text-lg text-gray-600 mb-4">{classData.courseTitle}</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="font-medium">Giảng viên:</span>
                  <span className="ml-1">{classData.instructor || 'Chưa phân công'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 012 0v4h4a1 1 0 010 2h-4v4a1 1 0 01-2 0V9H4a1 1 0 010-2h4z" />
                  </svg>
                  <span className="font-medium">Học sinh:</span>
                  <span className="ml-1">{classData.enrollmentCount || 0}/{classData.enrollmentLimit || 0}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-medium">Địa điểm:</span>
                  <span className="ml-1">{classData.location || 'Chưa xác định'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="font-medium">Phòng:</span>
                  <span className="ml-1">{classData.room || 'Chưa xác định'}</span>
                </div>
                <div className="flex flex-col text-sm text-gray-600">
                  <div className="flex items-center mb-1">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span className="font-medium">Tổ chức:</span>
                  </div>
                  <div className="ml-7">
                    <div className="font-medium text-gray-900">{classData.organization?.name || 'Chưa xác định'}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={getOrganizationTypeBadge(classData.organization?.type || 'internal')}>
                        {getOrganizationTypeText(classData.organization?.type || 'internal')}
                      </span>
                    </div>
                    {classData.organization?.department && (
                      <div className="text-xs text-gray-500 mt-1">
                        {classData.organization.department}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Attendance Tab */}
      {activeTab === 'attendance' && (
        <div className="space-y-6">
          {/* Attendance Overview */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Tổng quan điểm danh</h3>
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 flex items-center gap-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Nhập từ file
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-green-800 text-sm font-medium">Tổng buổi học</div>
                <div className="text-2xl font-bold text-green-900">{classData.schedules.length}</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-blue-800 text-sm font-medium">Đã điểm danh</div>
                <div className="text-2xl font-bold text-blue-900">
                  {Object.keys(attendanceData).length}
                </div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="text-yellow-800 text-sm font-medium">Tỷ lệ tham dự TB</div>
                <div className="text-2xl font-bold text-yellow-900">
                  {Object.keys(attendanceData).length > 0 
                    ? Math.round(Object.keys(attendanceData).reduce((sum, scheduleId) => 
                        sum + calculateAttendanceRate(scheduleId), 0) / Object.keys(attendanceData).length)
                    : 0}%
                </div>
              </div>
            </div>
          </div>

          {/* Schedule List with Attendance Actions */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Điểm danh theo buổi học</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {classData.schedules.map((schedule: any) => (
                <div key={schedule.id} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-medium text-gray-900">{schedule.topic}</h4>
                        <span className={getCourseTypeBadge(schedule.type)}>
                          {getCourseTypeText(schedule.type)}
                        </span>
                        {attendanceData[schedule.id] && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Đã điểm danh ({calculateAttendanceRate(schedule.id)}%)
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(schedule.date)} • {schedule.startTime} - {schedule.endTime}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {schedule.type === 'elearning' ? (
                        <button
                          onClick={() => simulateElearningAttendance(schedule.id)}
                          className="px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
                        >
                          Điểm danh tự động
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => setSelectedSchedule(selectedSchedule === schedule.id ? null : schedule.id)}
                            className="px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                          >
                            Điểm danh thủ công
                          </button>
                          <button
                            onClick={() => setFileUploadSchedule(schedule.id)}
                            className="px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100"
                          >
                            Nhập từ Excel
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Manual Attendance Interface */}
                  {selectedSchedule === schedule.id && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Điểm danh thủ công</h5>
                      <div className="space-y-2">
                        {classData.students.map((student: any) => {
                          const attendanceRecord = attendanceData[schedule.id]?.[student.id];
                          return (
                            <div key={student.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                                  <span className="text-xs font-medium text-gray-700">
                                    {student.name.split(' ').map((n: any) => n[0]).join('').toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{student.name}</div>
                                  <div className="text-xs text-gray-500">{student.department}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {attendanceRecord && (
                                  <span className={getAttendanceStatusBadge(attendanceRecord.status)}>
                                    {getAttendanceStatusText(attendanceRecord.status)}
                                  </span>
                                )}
                                <div className="flex gap-1">
                                  {(['present', 'late', 'absent', 'excused'] as const).map((status) => (
                                    <button
                                      key={status}
                                      onClick={() => markAttendance(schedule.id, student.id, status)}
                                      className={`px-2 py-1 text-xs font-medium rounded ${
                                        attendanceRecord?.status === status
                                          ? 'bg-indigo-600 text-white'
                                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                      }`}
                                    >
                                      {getAttendanceStatusText(status)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* File Upload Interface */}
                  {fileUploadSchedule === schedule.id && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Nhập điểm danh từ Excel</h5>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <div className="text-center">
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <div className="mt-2">
                            <label htmlFor="file-upload-modal" className="cursor-pointer">
                              <span className="mt-2 block text-sm font-medium text-gray-900">
                                {importFile ? importFile.name : 'Chọn file Excel'}
                              </span>
                              <input
                                id="file-upload-modal"
                                name="file-upload-modal"
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                className="sr-only"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    setImportFile(file);
                                  }
                                }}
                              />
                            </label>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Hỗ trợ Excel (.xlsx, .xls) hoặc CSV
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show attendance results if taken */}
                  {attendanceData[schedule.id] && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Kết quả điểm danh</h5>
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="text-green-800 text-xs font-medium">Có mặt</div>
                          <div className="text-lg font-bold text-green-900">
                            {Object.values(attendanceData[schedule.id]).filter((r: any) => r.status === 'present').length}
                          </div>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-3">
                          <div className="text-yellow-800 text-xs font-medium">Muộn</div>
                          <div className="text-lg font-bold text-yellow-900">
                            {Object.values(attendanceData[schedule.id]).filter((r: any) => r.status === 'late').length}
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3">
                          <div className="text-red-800 text-xs font-medium">Vắng mặt</div>
                          <div className="text-lg font-bold text-red-900">
                            {Object.values(attendanceData[schedule.id]).filter((r: any) => r.status === 'absent').length}
                          </div>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3">
                          <div className="text-blue-800 text-xs font-medium">Có phép</div>
                          <div className="text-lg font-bold text-blue-900">
                            {Object.values(attendanceData[schedule.id]).filter((r: any) => r.status === 'excused').length}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Import Attendance Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Nhập điểm danh từ file</h3>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setSelectedImportSession('');
                    setImportFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Session Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chọn buổi học
                  </label>
                  <select
                    value={selectedImportSession}
                    onChange={(e) => setSelectedImportSession(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">-- Chọn buổi học --</option>
                    {classData.schedules.map((schedule: any) => (
                      <option key={schedule.id} value={schedule.id}>
                        {formatDate(schedule.date)} - {schedule.topic}
                      </option>
                    ))}
                  </select>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chọn file Excel
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <div className="text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="mt-2">
                        <label htmlFor="file-upload-modal" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            {importFile ? importFile.name : 'Chọn file Excel'}
                          </span>
                          <input
                            id="file-upload-modal"
                            name="file-upload-modal"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="sr-only"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setImportFile(file);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Hỗ trợ Excel (.xlsx, .xls) hoặc CSV
                      </p>
                    </div>
                  </div>
                </div>

                {/* File Format Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-900">Định dạng file yêu cầu:</h4>
                      <div className="mt-1 text-sm text-blue-700">
                        <p>• Cột A: Mã học sinh</p>
                        <p>• Cột B: Họ tên</p>
                        <p>• Cột C: Trạng thái (Có mặt/Vắng mặt/Muộn/Có phép)</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between pt-4">
                  <button
                    onClick={downloadTemplate}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                  >
                    Tải mẫu file
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowImportModal(false);
                        setSelectedImportSession('');
                        setImportFile(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={handleImportAttendance}
                      disabled={!importFile || !selectedImportSession}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Nhập điểm danh
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Mô tả khóa học</h3>
              <p className="text-gray-600">{classData.description || 'Chưa có mô tả'}</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Mục tiêu học tập</h3>
              <ul className="space-y-2">
                {(classData.objectives || []).map((objective: any, index: any) => (
                  <li key={index} className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-600">{objective}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin lớp học</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Thời gian:</span>
                  <p className="text-sm text-gray-900">{formatDate(classData.startDate)} - {formatDate(classData.endDate)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Lịch học:</span>
                  <p className="text-sm text-gray-900">{classData.schedule}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Mã lớp:</span>
                  <p className="text-sm text-gray-900">#{classData.id}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Tổ chức:</span>
                  <p className="text-sm font-medium text-gray-900">{classData.organization?.name || 'Chưa xác định'}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={getOrganizationTypeBadge(classData.organization?.type || 'internal')}>
                      {getOrganizationTypeText(classData.organization?.type || 'internal')}
                    </span>
                  </div>
                  {classData.organization?.department && (
                    <p className="text-xs text-gray-500 mt-1">{classData.organization.department}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thống kê</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Tỷ lệ đăng ký:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {Math.round((classData.enrollmentCount / classData.enrollmentLimit) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-indigo-600 h-2 rounded-full" 
                    style={{ width: `${(classData.enrollmentCount / classData.enrollmentLimit) * 100}%` }}
                  ></div>
                </div>
                {(classData.schedules || []).length > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Tiến độ học tập:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {(classData.schedules || []).filter((s: any) => s.status === 'completed').length}/{(classData.schedules || []).length} buổi
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${((classData.schedules || []).filter((s: any) => s.status === 'completed').length / (classData.schedules || []).length) * 100}%` }}
                      ></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'students' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Danh sách học sinh</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Học sinh
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bộ phận
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Liên hệ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tham dự
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {classData.students.map((student: any) => {
                  // Calculate actual attendance from attendance data
                  const actualAttendance = Object.values(attendanceData).filter((scheduleAttendance: any) => {
                    const record = scheduleAttendance[student.id];
                    return record && (record.status === 'present' || record.status === 'late');
                  }).length;
                  const totalSessions = Object.keys(attendanceData).length;
                  
                  return (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {student.name.split(' ').map((n: any) => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{student.name}</div>
                            <div className="text-sm text-gray-500">{student.position}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.department}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.email}</div>
                        <div className="text-sm text-gray-500">{student.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {actualAttendance || student.attendance.present}/{totalSessions || student.attendance.total}
                        </div>
                        <div className="text-sm text-gray-500">
                          {totalSessions > 0 
                            ? Math.round((actualAttendance / totalSessions) * 100)
                            : Math.round((student.attendance.present / student.attendance.total) * 100)
                          }%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStudentStatusBadge(student.status)}>
                          {getStudentStatusText(student.status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'schedule' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Lịch học chi tiết</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {classData.schedules.map((schedule: any) => (
              <div key={schedule.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${
                        schedule.status === 'completed' ? 'bg-green-500' :
                        schedule.status === 'scheduled' ? 'bg-blue-500' : 'bg-red-500'
                      }`}></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium text-gray-900">{schedule.topic}</h4>
                          <span className={getCourseTypeBadge(schedule.type)}>
                            {getCourseTypeText(schedule.type)}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 012 0v4h4a1 1 0 010 2h-4v4a1 1 0 01-2 0V9H4a1 1 0 010-2h4z" />
                          </svg>
                          <span>{formatDate(schedule.date)} • {schedule.startTime} - {schedule.endTime}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getCourseTypeIcon(schedule.type)} />
                          </svg>
                          <span>{schedule.room}</span>
                          {schedule.type === 'online' && schedule.meetingLink && (
                            <a 
                              href={schedule.meetingLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="ml-2 text-blue-600 hover:text-blue-800 underline"
                            >
                              Tham gia
                            </a>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          <span>Giảng viên: {schedule.instructor}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    {(attendanceData[schedule.id] || schedule.attendanceRate) && (
                      <div className="text-sm text-gray-900">
                        Tham dự: {attendanceData[schedule.id] ? calculateAttendanceRate(schedule.id) : schedule.attendanceRate}%
                      </div>
                    )}
                    <div className="text-sm text-gray-500">
                      {schedule.status === 'completed' ? 'Đã hoàn thành' :
                       schedule.status === 'scheduled' ? 'Đã lên lịch' : 'Đã hủy'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'materials' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Tài liệu học tập</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(classData.materials || []).map((material: any, index: any) => (
                <div key={index} className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                  <svg className="h-8 w-8 text-indigo-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{material}</h4>
                    <p className="text-sm text-gray-500">Tài liệu học tập</p>
                  </div>
                  <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">
                    Tải xuống
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Assessment Tab */}
      {activeTab === 'assessment' && (
        <div className="space-y-6">
          {/* Assessment Overview */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Quản lý đánh giá</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAssessmentImportModal(true)}
                  className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Nhập điểm
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-blue-800 text-sm font-medium">Tổng bài đánh giá</div>
                <div className="text-2xl font-bold text-blue-900">{mockAssessments.length}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-green-800 text-sm font-medium">Đã chấm điểm</div>
                <div className="text-2xl font-bold text-green-900">
                  {Object.keys(assessmentData).length}
                </div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="text-yellow-800 text-sm font-medium">Điểm TB lớp</div>
                <div className="text-2xl font-bold text-yellow-900">
                  {classData.students.length > 0 
                    ? Math.round(classData.students.reduce((sum: any, student: any) => 
                        sum + calculateStudentGPA(student.id), 0) / classData.students.length)
                    : 0}
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-purple-800 text-sm font-medium">Tỷ lệ đạt</div>
                <div className="text-2xl font-bold text-purple-900">
                  {classData.students.length > 0 
                    ? Math.round((classData.students.filter((student: any) => 
                        calculateStudentGPA(student.id) >= 70).length / classData.students.length) * 100)
                    : 0}%
                </div>
              </div>
            </div>
          </div>

          {/* Assessment List */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Danh sách đánh giá</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {mockAssessments.map((assessment) => (
                <div key={assessment.id} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-medium text-gray-900">{assessment.title}</h4>
                        <span className={getAssessmentTypeBadge(assessment.type)}>
                          {getAssessmentTypeText(assessment.type)}
                        </span>
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          {assessment.weight}%
                        </span>
                        {assessmentData[assessment.id] && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Đã chấm điểm
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">
                        {assessment.description}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Điểm tối đa: {assessment.maxScore}</span>
                        <span>Điểm đạt: {assessment.passingScore}</span>
                        {assessment.dueDate && (
                          <span>Hạn nộp: {formatDate(assessment.dueDate)}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedAssessment(selectedAssessment === assessment.id ? null : assessment.id)}
                        className="px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                      >
                        Chấm điểm
                      </button>
                      {assessmentData[assessment.id] && (
                        <button
                          onClick={() => exportAssessmentResults(assessment.id)}
                          className="px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100"
                        >
                          Xuất kết quả
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Manual Grading Interface */}
                  {selectedAssessment === assessment.id && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Chấm điểm thủ công</h5>
                      <div className="space-y-3">
                        {classData.students.map((student: any) => {
                          const result = assessmentData[assessment.id]?.[student.id];
                          return (
                            <div key={student.id} className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-md">
                              <div className="flex items-center flex-1">
                                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                                  <span className="text-xs font-medium text-gray-700">
                                    {student.name.split(' ').map((n: any) => n[0]).join('').toUpperCase()}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">{student.name}</div>
                                  <div className="text-xs text-gray-500">{student.department}</div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                {result && (
                                  <div className="text-right">
                                    <div className={`text-sm font-medium ${getGradeColor(result.score, result.maxScore, assessment.passingScore)}`}>
                                      {result.score}/{result.maxScore} ({Math.round((result.score / result.maxScore) * 100)}%)
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {result.score >= assessment.passingScore ? 'Đạt' : 'Không đạt'}
                                    </div>
                                  </div>
                                )}
                                
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max={assessment.maxScore}
                                    placeholder="Điểm"
                                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    defaultValue={result?.score || ''}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        const score = parseFloat((e.target as HTMLInputElement).value);
                                        if (!isNaN(score) && score >= 0 && score <= assessment.maxScore) {
                                          addAssessmentScore(assessment.id, student.id, score, assessment.maxScore);
                                        }
                                      }
                                    }}
                                  />
                                  <span className="text-sm text-gray-500">/{assessment.maxScore}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Assessment Results Summary */}
                  {assessmentData[assessment.id] && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Thống kê kết quả</h5>
                      <div className="grid grid-cols-5 gap-4 text-center">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <div className="text-blue-800 text-xs font-medium">Điểm TB</div>
                          <div className="text-lg font-bold text-blue-900">
                            {Object.values(assessmentData[assessment.id]).length > 0 
                              ? Math.round((Object.values(assessmentData[assessment.id]) as any[])
                                  .reduce((sum: any, result: any) => sum + (result.score / result.maxScore) * 100, 0) 
                                  / Object.values(assessmentData[assessment.id]).length)
                              : 0}
                          </div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="text-green-800 text-xs font-medium">Đạt</div>
                          <div className="text-lg font-bold text-green-900">
                            {Object.values(assessmentData[assessment.id])
                              .filter((result: any) => result.score >= assessment.passingScore).length}
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3">
                          <div className="text-red-800 text-xs font-medium">Không đạt</div>
                          <div className="text-lg font-bold text-red-900">
                            {Object.values(assessmentData[assessment.id])
                              .filter((result: any) => result.score < assessment.passingScore).length}
                          </div>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-3">
                          <div className="text-yellow-800 text-xs font-medium">Tỷ lệ đạt</div>
                          <div className="text-lg font-bold text-yellow-900">
                            {Object.values(assessmentData[assessment.id]).length > 0 
                              ? Math.round((Object.values(assessmentData[assessment.id])
                                  .filter((result: any) => result.score >= assessment.passingScore).length
                                  / Object.values(assessmentData[assessment.id]).length) * 100)
                              : 0}%
                          </div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3">
                          <div className="text-purple-800 text-xs font-medium">Đã chấm</div>
                          <div className="text-lg font-bold text-purple-900">
                            {Object.values(assessmentData[assessment.id]).length}/{classData.students.length}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Student Assessment Overview */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Tổng hợp điểm học sinh</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Học sinh
                    </th>
                    {mockAssessments.map((assessment) => (
                      <th key={assessment.id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {assessment.title}
                        <div className="text-xs font-normal text-gray-400">({assessment.weight}%)</div>
                      </th>
                    ))}
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tổng điểm
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kết quả
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {classData.students.map((student: any) => {
                    const gpa = calculateStudentGPA(student.id);
                    return (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                              <span className="text-xs font-medium text-gray-700">
                                {student.name.split(' ').map((n: any) => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{student.name}</div>
                              <div className="text-sm text-gray-500">{student.department}</div>
                            </div>
                          </div>
                        </td>
                        {mockAssessments.map((assessment) => {
                          const result = assessmentData[assessment.id]?.[student.id];
                          return (
                            <td key={assessment.id} className="px-6 py-4 whitespace-nowrap text-center">
                              {result ? (
                                <div>
                                  <div className={`text-sm font-medium ${getGradeColor(result.score, result.maxScore, assessment.passingScore)}`}>
                                    {result.score}/{result.maxScore}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {Math.round((result.score / result.maxScore) * 100)}%
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className={`text-sm font-medium ${gpa >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                            {gpa}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            gpa >= 70 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {gpa >= 70 ? 'Đạt' : 'Không đạt'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Import Modal */}
      {showAssessmentImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Nhập điểm từ file</h3>
                <button
                  onClick={() => {
                    setShowAssessmentImportModal(false);
                    setSelectedImportAssessment('');
                    setAssessmentImportFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Assessment Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chọn bài đánh giá
                  </label>
                  <select
                    value={selectedImportAssessment}
                    onChange={(e) => setSelectedImportAssessment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">-- Chọn bài đánh giá --</option>
                    {mockAssessments.map((assessment) => (
                      <option key={assessment.id} value={assessment.id}>
                        {assessment.title} (Max: {assessment.maxScore})
                      </option>
                    ))}
                  </select>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chọn file điểm
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <div className="text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="mt-2">
                        <label htmlFor="assessment-file-upload" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            {assessmentImportFile ? assessmentImportFile.name : 'Chọn file Excel'}
                          </span>
                          <input
                            id="assessment-file-upload"
                            name="assessment-file-upload"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="sr-only"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setAssessmentImportFile(file);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Hỗ trợ Excel (.xlsx, .xls) hoặc CSV
                      </p>
                    </div>
                  </div>
                </div>

                {/* File Format Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-900">Định dạng file yêu cầu:</h4>
                      <div className="mt-1 text-sm text-blue-700">
                        <p>• Cột A: Mã học sinh</p>
                        <p>• Cột B: Họ tên</p>
                        <p>• Cột C: Điểm số</p>
                        <p>• Cột D: Nhận xét (tùy chọn)</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between pt-4">
                  <button
                    onClick={downloadAssessmentTemplate}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                  >
                    Tải mẫu file
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowAssessmentImportModal(false);
                        setSelectedImportAssessment('');
                        setAssessmentImportFile(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={handleImportAssessmentResults}
                      disabled={!assessmentImportFile || !selectedImportAssessment}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Nhập điểm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassDetailPage; 